import type { NewRequisitionForm } from "../../recoil/types";
import { CLEARED_ANEUPLOIDY_ADD_ONS } from "./fetalAntigenConstants";
import {
  EXPANDED_CARRIER_PANEL,
  EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS,
  type ExpandedCarrierPanel,
  type ExpandedCarrierPanelAccountFlag,
} from "./types";

/**
 * Unity Complete presets. Selecting one replaces managed carrier/aneuploidy
 * fields and selects the requested 22q variant; other aneuploidy add-ons are
 * never included.
 */

export const UNITY_COMPLETE_BUNDLE_KEYS = [
  "5-22q",
  "13-22q",
  "14-22q",
  "130-22q",
  "611-22q",
  "5",
  "13",
  "14",
  "130",
  "611",
] as const;

export type UnityCompleteBundleKey =
  (typeof UNITY_COMPLETE_BUNDLE_KEYS)[number];

type ExpandedCarrierPanelAccountFlags = Pick<
  NewRequisitionForm,
  ExpandedCarrierPanelAccountFlag
>;

type UnityCompleteBundle = {
  key: UnityCompleteBundleKey;
  label: string;
  /** null = no expanded carrier panel. */
  expandedCarrierPanel: ExpandedCarrierPanel | null;
  isFragileX: boolean;
  isAcogPlus: boolean;
  aneuploidyNIPT: boolean;
  is22q: boolean;
};

export const UNITY_COMPLETE_BUNDLES: Record<
  UnityCompleteBundleKey,
  UnityCompleteBundle
> = {
  "5-22q": {
    key: "5-22q",
    label: "Aneuploidy NIPT w/ 22q11.2 + 5-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: true,
  },
  "13-22q": {
    key: "13-22q",
    label: "Aneuploidy NIPT w/ 22q11.2 + 13-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: false,
    isAcogPlus: true,
    aneuploidyNIPT: true,
    is22q: true,
  },
  "14-22q": {
    key: "14-22q",
    label: "Aneuploidy NIPT w/ 22q11.2 + 14-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: true,
    isAcogPlus: true,
    aneuploidyNIPT: true,
    is22q: true,
  },
  "130-22q": {
    key: "130-22q",
    label: "Aneuploidy NIPT w/ 22q11.2 + 130-gene Panel",
    expandedCarrierPanel: EXPANDED_CARRIER_PANEL.CARRIER_130,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: true,
  },
  "611-22q": {
    key: "611-22q",
    label: "Aneuploidy NIPT w/ 22q11.2 + 611-gene Panel",
    expandedCarrierPanel: EXPANDED_CARRIER_PANEL.CARRIER_611,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: true,
  },
  "5": {
    key: "5",
    label: "Aneuploidy NIPT + 5-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: false,
  },
  "13": {
    key: "13",
    label: "Aneuploidy NIPT + 13-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: false,
    isAcogPlus: true,
    aneuploidyNIPT: true,
    is22q: false,
  },
  "14": {
    key: "14",
    label: "Aneuploidy NIPT + 14-gene Panel",
    expandedCarrierPanel: null,
    isFragileX: true,
    isAcogPlus: true,
    aneuploidyNIPT: true,
    is22q: false,
  },
  "130": {
    key: "130",
    label: "Aneuploidy NIPT + 130-gene Panel",
    expandedCarrierPanel: EXPANDED_CARRIER_PANEL.CARRIER_130,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: false,
  },
  "611": {
    key: "611",
    label: "Aneuploidy NIPT + 611-gene Panel",
    expandedCarrierPanel: EXPANDED_CARRIER_PANEL.CARRIER_611,
    isFragileX: false,
    isAcogPlus: false,
    aneuploidyNIPT: true,
    is22q: false,
  },
};

export const UNITY_COMPLETE_BUNDLE_OPTIONS = UNITY_COMPLETE_BUNDLE_KEYS.map(
  (key) => UNITY_COMPLETE_BUNDLES[key],
);

const EXPANDED_CARRIER_PANELS: ExpandedCarrierPanel[] = [
  EXPANDED_CARRIER_PANEL.CARRIER_113_CUSTOM,
  EXPANDED_CARRIER_PANEL.CARRIER_130,
  EXPANDED_CARRIER_PANEL.CARRIER_611,
];

type UnityCompleteBundleAvailabilityFields = Pick<
  NewRequisitionForm,
  "isFragileXAccount" | "sex"
> &
  ExpandedCarrierPanelAccountFlags;

/** Fragile X needs an account configuration; FX is male-ineligible. */
export const isUnityCompleteBundleAvailable = (
  bundleKey: UnityCompleteBundleKey,
  form: UnityCompleteBundleAvailabilityFields,
): boolean => {
  const bundle = UNITY_COMPLETE_BUNDLES[bundleKey];
  if (bundle.isFragileX && !form.isFragileXAccount) {
    return false;
  }
  if (bundle.isFragileX && form.sex === "Male") {
    return false;
  }
  const expandedPanelAccountFlag = bundle.expandedCarrierPanel
    ? EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS[bundle.expandedCarrierPanel]
    : null;
  if (expandedPanelAccountFlag && !form[expandedPanelAccountFlag]) {
    return false;
  }
  return true;
};

export const getAvailableUnityCompleteBundleOptions = (
  form: UnityCompleteBundleAvailabilityFields,
) =>
  UNITY_COMPLETE_BUNDLE_OPTIONS.filter((option) =>
    isUnityCompleteBundleAvailable(option.key, form),
  );

export const getVisibleExpandedCarrierPanels = (
  form: ExpandedCarrierPanelAccountFlags,
  selectedExpandedCarrierPanel: ExpandedCarrierPanel | null,
): ExpandedCarrierPanel[] =>
  EXPANDED_CARRIER_PANELS.filter(
    (expandedCarrierPanel) =>
      form[EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS[expandedCarrierPanel]] ||
      expandedCarrierPanel === selectedExpandedCarrierPanel,
  );

type UnityCompleteBundleMatchFields = Pick<
  NewRequisitionForm,
  | "carrierScreen"
  | "expandedCarrierPanel"
  | "isFragileX"
  | "isAcogPlus"
  | "aneuploidyNIPT"
  | "is22q"
>;

/** Match the base preset independently of optional aneuploidy add-ons. */
export const matchUnityCompleteBundle = (
  form: UnityCompleteBundleMatchFields,
): UnityCompleteBundleKey | null => {
  if (!form.carrierScreen) {
    return null;
  }

  for (const bundleKey of UNITY_COMPLETE_BUNDLE_KEYS) {
    const bundle = UNITY_COMPLETE_BUNDLES[bundleKey];
    if (
      form.expandedCarrierPanel === bundle.expandedCarrierPanel &&
      form.isFragileX === bundle.isFragileX &&
      form.isAcogPlus === bundle.isAcogPlus &&
      form.aneuploidyNIPT === bundle.aneuploidyNIPT &&
      Boolean(form.is22q) === bundle.is22q
    ) {
      return bundleKey;
    }
  }
  return null;
};

/** Apply a preset, select its 22q variant, and clear other add-ons. */
export const getUnityCompleteBundlePatch = (
  bundleKey: UnityCompleteBundleKey,
): Partial<NewRequisitionForm> => {
  const bundle = UNITY_COMPLETE_BUNDLES[bundleKey];

  return {
    ...CLEARED_ANEUPLOIDY_ADD_ONS,
    carrierScreen: true,
    expandedCarrierPanel: bundle.expandedCarrierPanel,
    isFragileX: bundle.isFragileX,
    isAcogPlus: bundle.isAcogPlus,
    aneuploidyNIPT: bundle.aneuploidyNIPT,
    is22q: bundle.is22q,
    cfDNAOptOut: false,
    isRhdOnlyOrder: false,
    O285_carrier: true,
    Z360: bundle.aneuploidyNIPT,
  };
};
